import React from 'react';
import {
  Input,
  Dropdown,
  DropdownMenu,
  FormFeedback,
  InputGroupAddon
} from 'reactstrap';
import { usePhoneInput } from '../hooks/usePhoneInput';
import {
  getCountryFlagUrl,
  getCountryByDialCode
} from 'services/ServiceCOUNTRYS';
import { CountrySelectDropdown, NumberInput, PhoneInputGroup } from './styles';
import { RiArrowDropDownLine } from 'react-icons/ri';
import { IoMdClose } from 'react-icons/io';
import { FiFlag } from 'react-icons/fi';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { hexToRgba } from 'utils/fuctions/hexToRgb';

/**
 * Componente PhoneInput para manipular entrada de números de telefone internacionais com seleção de país.
 *
 * @component
 * @param {Object} props - Propriedades do componente.
 * @param {string} [props.placeholder] - Texto de placeholder para o campo de número de telefone.
 * @param {boolean} [props.required] - Se o campo é obrigatório.
 * @param {string} [props.className] - Classes CSS adicionais para o container do componente.
 * @param {string} [props.error] - Mensagem de erro a ser exibida.
 * @param {boolean} [props.disabled] - Se o campo está desabilitado.
 * @param {Object} [props.country] - Objeto de país padrão para a seleção inicial.
 * @param {Function} [props.valueController=() => {}] - Callback para atualizar o valor no controlador pai.
 * @param {string} [props.name] - Nome do campo para o controle de formulário.
 * @param {Object} [props.control] - Objeto de controle de formulário (ex: do react-hook-form).
 * @param {boolean} [props.showErrors=false] - Se deve mostrar as mensagens de erro. Geralmente true após tentativa de salvar.
 * @param {Function} [props.onError=() => {}] - Callback chamado quando o estado de validação muda, com o erro ou null.
 * @param {boolean} [props.validateOnSubmit=false] - Se deve validar quando o formulário for submetido.
 * @param {Object} [props.innerRef] - Ref para acessar métodos do componente, como validate().
 * @param {boolean} [props.canCleanData=true] - Se o botão de limpar dados deve ser exibido.
 *
 * @returns {JSX.Element} O componente PhoneInput renderizado.
 */

export function PhoneInput({
  required,
  className,
  error: externalError,
  disabled,
  valueController = () => {},
  name,
  control,
  watch,
  value,
  showErrors = false,
  onError = () => {},
  validateOnSubmit = false,
  innerRef,
  canCleanData = true
}) {
  const [internalError, setInternalError] = React.useState('');
  const [fieldTouched, setFieldTouched] = React.useState(false);

  const validate = React.useCallback(() => {
    const { ddi, number } = getCurrentValues();
    return validatePhoneField(ddi, number);
  }, []);

  React.useImperativeHandle(
    innerRef,
    () => ({
      validate,
      getCurrentValues,
      hasError: () => !!internalError
    }),
    [validate, internalError]
  );

  React.useEffect(() => {
    if (validateOnSubmit) {
      validate();
      setFieldTouched(true);
    }
  }, [validateOnSubmit, validate]);

  React.useEffect(() => {
    const validateCurrentValues = () => {
      const { ddi, number } = getCurrentValues();
      validatePhoneField(ddi, number);
    };

    validateCurrentValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, watchedDdi, watchedNumber, required]);
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    searchTerm,
    setSearchTerm,
    countries,
    filteredCountries,
    isLoading,
    dropdownRef,
    inputRef
  } = usePhoneInput();

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const ddiFieldName = `${name}_ddi`;
  const numberFieldName = `${name}_number`;
  const watchedDdi = watch(ddiFieldName);
  const watchedNumber = watch(numberFieldName);

  const getCurrentValues = () => {
    let ddi = value?.ddi;
    let number = value?.number;

    if (!ddi)
      ddi =
        watchedDdi ||
        control?.getValues(ddiFieldName) ||
        control?.getValues(name)?.ddi ||
        '';

    if (!number)
      number =
        watchedNumber ||
        control?.getValues(numberFieldName) ||
        control?.getValues(name)?.number ||
        '';

    return { ddi, number };
  };

  const validatePhoneField = (ddiValue, numberValue) => {
    let errorMessage = '';
    if (required) {
      if (!ddiValue && !numberValue) {
        errorMessage = 'Campo obrigatório';
      } else if (numberValue && !ddiValue) {
        errorMessage = 'Selecione o código do país (DDI)';
      } else if (ddiValue && !numberValue) {
        errorMessage = 'Digite o número de telefone';
      }
    }

    setInternalError(errorMessage);

    onError(errorMessage || null);

    return !errorMessage;
  };

  const handlePhoneChange = (phoneNumber) => {
    const { ddi: currentDdiValue } = getCurrentValues();
    const numbersOnly = phoneNumber.replace(/\D/g, '');

    setFieldTouched(true);

    validatePhoneField(currentDdiValue, numbersOnly);

    valueController(numberFieldName, numbersOnly);
    valueController(name, {
      ddi: currentDdiValue,
      number: numbersOnly
    });
  };

  const handleClearAll = () => {
    valueController(ddiFieldName, '');
    valueController(numberFieldName, '');
    valueController(name, {
      ddi: '',
      number: ''
    });

    if (control && control.setValue) {
      control.setValue(ddiFieldName, '', { shouldValidate: true });
      control.setValue(numberFieldName, '', { shouldValidate: true });
      control.setValue(name, { ddi: '', number: '' }, { shouldValidate: true });
    }

    setFieldTouched(true);

    validatePhoneField('', '');

    if (inputRef.current) inputRef.current.focus();
  };

  const handleCountrySelect = (dialCode) => {
    const { number: currentNumberValue } = getCurrentValues();

    setFieldTouched(true);

    validatePhoneField(dialCode, currentNumberValue);

    valueController(ddiFieldName, dialCode);
    valueController(name, {
      ddi: dialCode,
      number: currentNumberValue
    });

    setIsDropdownOpen(false);

    if (inputRef.current) inputRef.current.focus();
  };

  const { ddi: currentDdiForRender, number: currentNumberForRender } =
    getCurrentValues();

  const selectedCountry =
    currentDdiForRender === ''
      ? null
      : countries?.find((country) => country.dialCode === currentDdiForRender);

  return (
    <div className={className}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <PhoneInputGroup style={{ flex: 1 }}>
          <InputGroupAddon addonType="prepend">
            <Controller
              name={ddiFieldName}
              control={control}
              value={getCurrentValues()?.ddi}
              defaultValue={
                currentDdiForRender === '' ? '' : currentDdiForRender
              }
              render={({ field }) => (
                <Dropdown
                  isOpen={isDropdownOpen}
                  toggle={() => setIsDropdownOpen(!isDropdownOpen)}
                  innerRef={dropdownRef}
                >
                  <CountrySelectDropdown
                    disabled={disabled || isLoading}
                    toggle={false}
                  >
                    {selectedCountry && field?.value !== '' ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {getCountryFlagUrl(selectedCountry?.code) ? (
                          <img
                            src={getCountryFlagUrl(selectedCountry?.code)}
                            alt={selectedCountry?.name}
                            style={{
                              width: '20px',
                              height: '15px'
                            }}
                          />
                        ) : (
                          <FiFlag size={20} color="#bdbdbd" />
                        )}
                        <span>
                          {`+${field?.value || selectedCountry?.dialCode}` ||
                            '+'}
                        </span>
                        <RiArrowDropDownLine
                          size={18}
                          style={{ marginLeft: '-4px' }}
                        />
                      </div>
                    ) : (
                      <div>
                        <FiFlag size={16} color="#bdbdbd" />
                        <RiArrowDropDownLine size={18} />
                      </div>
                    )}
                  </CountrySelectDropdown>
                  <DropdownMenu>
                    <div className="px-2 py-1">
                      <Input
                        bsSize="sm"
                        placeholder="Buscar país..."
                        value={searchTerm}
                        style={{
                          borderColor: hexToRgba(
                            systemConfiguration?.primary_color,
                            0.6
                          )
                        }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {filteredCountries.map((country) => (
                        <button
                          key={country.code}
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => {
                            handleCountrySelect(country.dialCode);
                          }}
                          type="button"
                        >
                          <img
                            src={getCountryFlagUrl(country.code)}
                            alt={country.name}
                            style={{
                              width: '20px',
                              height: '15px'
                            }}
                          />
                          <span>{country.name}</span>
                          <small className="ml-auto text-muted">
                            {`+${country.dialCode}` || '+'}
                          </small>
                        </button>
                      ))}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              )}
            />
          </InputGroupAddon>

          <Controller
            name={numberFieldName}
            control={control}
            defaultValue={currentNumberForRender}
            render={({ field }) => (
              <NumberInput
                {...field}
                value={getCurrentValues()?.number || ''}
                innerRef={inputRef}
                type="text"
                placeholder={
                  currentDdiForRender
                    ? getCountryByDialCode(currentDdiForRender)?.placeholder
                    : 'Número de telefone'
                }
                onChange={(e) => {
                  const filteredValue = e.target.value.replace(
                    /[^0-9()\-+\s]/g,
                    ''
                  );

                  e.target.value = filteredValue;

                  handlePhoneChange(filteredValue);
                }}
                required={required}
                disabled={disabled || isLoading}
              />
            )}
          />
        </PhoneInputGroup>

        {canCleanData &&
          (getCurrentValues()?.number || getCurrentValues()?.ddi) && (
            <div
              style={{
                marginLeft: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={handleClearAll}
              title="Limpar campos"
            >
              <IoMdClose
                size={16}
                color="#6c757d"
                style={{ marginLeft: '-50px', zIndex: 1 }}
              />
            </div>
          )}
      </div>

      {(showErrors || fieldTouched) && (externalError || internalError) && (
        <FormFeedback style={{ display: 'block' }}>
          {externalError || internalError}
        </FormFeedback>
      )}
    </div>
  );
}
